import React from 'react';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { blue, green, orange, red } from '@mui/material/colors';

import currencyFormatter from '~/utils/currencyFormatter';

import { audMoney } from '../../constants';

import type { ForecastOptionTotals } from './computeForecastTotals';
import { MARGIN_PCT_CRITICAL, MARGIN_PCT_WARNING, MIN_VISIBLE_DELTA } from './forecastingModel';

export interface ForecastOptionColumn {
  index: number;
  name: string;
  totals: ForecastOptionTotals;
}

interface Props {
  columns: Array<ForecastOptionColumn>;
  contractCurrency: string;
}

type MetricRow = {
  label: string;
  read: (totals: ForecastOptionTotals) => number | null;
  format: (value: number) => string;
  compareAs: 'ratio' | 'points';
  colorByMargin?: boolean;
  hidden?: boolean;
};

const OPTION_COLORS = [blue[700], orange[800], green[700]];

const HEAD_CELL_SX = { fontWeight: 600 };

const LABEL_CELL_SX = { whiteSpace: 'nowrap' };

const FIGURE_SX = { fontFamily: 'monospace', whiteSpace: 'nowrap' };

const marginColor = (pctMargin: number) => {
  if (pctMargin < MARGIN_PCT_CRITICAL) {
    return red[700];
  }

  return pctMargin < MARGIN_PCT_WARNING ? orange[800] : green[700];
};

function deltaLabel(compareAs: MetricRow['compareAs'], value: number | null, baseline: number | null): string | null {
  if (value === null || baseline === null) {
    return null;
  }

  if (compareAs === 'points') {
    const difference = value - baseline;
    return Math.abs(difference) < MIN_VISIBLE_DELTA ? null : `${difference > 0 ? '+' : ''}${difference.toFixed(1)}pts`;
  }

  if (baseline === 0) {
    return null;
  }

  const percentChange = ((value - baseline) / baseline) * 100;
  return Math.abs(percentChange) < MIN_VISIBLE_DELTA
    ? null
    : `${percentChange > 0 ? '+' : ''}${percentChange.toFixed(1)}%`;
}

export default function ForecastMetricsTable({ columns, contractCurrency }: Props) {
  const isAudContract = contractCurrency === 'AUD';
  const contractMoney = (value: number) => currencyFormatter(contractCurrency, value, 2);
  const decimal = (value: number) => value.toFixed(2);

  const rows: Array<MetricRow> = [
    { label: 'Packages', read: (t) => t.packages, format: (v) => String(v), compareAs: 'ratio' },
    { label: 'Room nights', read: (t) => t.roomNights, format: decimal, compareAs: 'ratio' },
    { label: 'Avg LOS', read: (t) => t.avgLos, format: decimal, compareAs: 'ratio' },
    {
      label: `Blended ADR (${contractCurrency})`,
      read: (t) => t.blendedAdrContract,
      format: contractMoney,
      compareAs: 'ratio',
      hidden: isAudContract,
    },
    { label: 'Blended ADR (AUD)', read: (t) => t.blendedAdrAud, format: audMoney, compareAs: 'ratio' },
    { label: 'TTV (AUD)', read: (t) => t.ttvAud, format: audMoney, compareAs: 'ratio' },
    {
      label: `Vendor revenue (${contractCurrency})`,
      read: (t) => t.vendorRevenueContract,
      format: contractMoney,
      compareAs: 'ratio',
      hidden: isAudContract,
    },
    { label: 'Vendor revenue (AUD)', read: (t) => t.vendorRevenueAud, format: audMoney, compareAs: 'ratio' },
    {
      label: 'Bundled experience cost (AUD)',
      read: (t) => t.bundledCostAud,
      format: audMoney,
      compareAs: 'ratio',
      hidden: columns.every((column) => column.totals.bundledCostAud === 0),
    },
    {
      label: 'Override comms (AUD)',
      read: (t) => t.overrideCommsAud,
      format: audMoney,
      compareAs: 'ratio',
      hidden: columns.every((column) => column.totals.overrideCommsPct === 0),
    },
    { label: 'Gross margin (AUD)', read: (t) => t.grossMarginAud, format: audMoney, compareAs: 'ratio' },
    {
      label: '% Margin',
      read: (t) => t.pctMargin,
      format: (v) => `${v.toFixed(1)}%`,
      compareAs: 'points',
      colorByMargin: true,
    },
  ];

  const [baseline] = columns;

  return (
    <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_CELL_SX, ...LABEL_CELL_SX }}>Metric</TableCell>
            {columns.map((column) => (
              <TableCell
                align="right"
                key={column.index}
                sx={{ ...HEAD_CELL_SX, color: OPTION_COLORS[column.index] ?? 'text.primary' }}
              >
                {column.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows
            .filter((row) => !row.hidden)
            .map((row) => (
              <TableRow key={row.label}>
                <TableCell sx={LABEL_CELL_SX}>{row.label}</TableCell>
                {columns.map((column) => {
                  const value = row.read(column.totals);
                  const delta =
                    column.index === baseline.index
                      ? null
                      : deltaLabel(row.compareAs, value, row.read(baseline.totals));

                  return (
                    <TableCell
                      align="right"
                      key={column.index}
                      sx={{
                        ...FIGURE_SX,
                        ...(row.colorByMargin && value !== null ? { color: marginColor(value) } : {}),
                      }}
                    >
                      {value === null ? '—' : row.format(value)}
                      {delta && (
                        <Typography color="text.secondary" component="span" sx={{ ml: 1 }} variant="caption">
                          {delta}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
